import React, { useState } from 'react';
import { FormApi } from 'final-form';
import { Button, ButtonProps, Popover, Typography, List, ListItem, ListItemText, Tooltip } from '@material-ui/core';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';

// Explicit request: Save used to grey out or fail without saying why. This keeps the existing button
// exactly as it was (same props, same click handler) and adds two things:
//  - when it is disabled, hovering it says why (disabledReason);
//  - when it is clicked while the form has validation errors, a small list shows every field that needs
//    attention - clicking an entry expands its panel (if collapsed), scrolls to the field and focuses it.
interface SaveButtonWithValidationSummaryProps extends ButtonProps {
    // not named `form`: that is already a (string) attribute of a button element
    formApi: FormApi<any>; // tslint:disable-line: no-any
    disabledReason?: string;
}

interface FieldToFix {
    name: string;
    label: string;
}

const FORM_SELECTOR = '#enquiryForm';

// 'firstName' -> 'First name'
const humanizeFieldName = (name: string): string => {
    const spaced = name.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/[_-]+/g, ' ').trim();
    return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
};

const findFieldElement = (name: string): HTMLElement | null => (
    document.querySelector(`${FORM_SELECTOR} [name="${name}"]`) as HTMLElement | null
);

export const resolveFieldLabel = (name: string): string => {
    const element = findFieldElement(name);
    const formControl = element ? element.closest('.MuiFormControl-root') : null;
    const labelElement = formControl ? formControl.querySelector('label') : null;
    const cleaned = ((labelElement && labelElement.textContent) || '').replace(/\s*\*\s*$/, '').trim();
    return cleaned || humanizeFieldName(name);
};

// react-final-form errors can be nested (e.g. custom fields) - report the top-level field that has any error
export const collectErrorFieldNames = (errors: object | undefined): string[] => (
    errors ? Object.keys(errors).filter((key) => {
        const value = (errors as Record<string, unknown>)[key];
        return value !== undefined && value !== null && value !== '';
    }) : []
);

export const goToField = (name: string): void => {
    const element = findFieldElement(name);
    if (!element) {
        return;
    }
    const accordion = element.closest('.MuiAccordion-root');
    const summary = accordion ? accordion.querySelector('.MuiAccordionSummary-root') as HTMLElement | null : null;
    const isCollapsed = !!accordion && !accordion.classList.contains('Mui-expanded');
    if (isCollapsed && summary) {
        summary.click();
    }
    // give an accordion that was just opened time to lay out before scrolling to it
    window.setTimeout(() => {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        element.focus({ preventScroll: true });
    }, isCollapsed ? 300 : 0);
};

export const SaveButtonWithValidationSummary: React.FC<SaveButtonWithValidationSummaryProps> = ({ formApi, disabledReason, onClick, disabled, children, ...buttonProps }) => {
    const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null);
    const [issues, setIssues] = useState<FieldToFix[]>([]);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        const clickedButton = event.currentTarget;
        if (onClick) {
            onClick(event);
        }
        const names = collectErrorFieldNames(formApi.getState().errors);
        if (names.length) {
            setIssues(names.map((name) => ({ name, label: resolveFieldLabel(name) })));
            setAnchorElement(clickedButton);
        }
    };

    const button = (
        <Button {...buttonProps} disabled={disabled} onClick={handleClick}>
            {children}
        </Button>
    );

    return (
        <>
            {disabled && disabledReason ? (
                <Tooltip title={disabledReason} arrow={true}>
                    <span>{button}</span>
                </Tooltip>
            ) : button}
            <Popover
                open={!!anchorElement}
                anchorEl={anchorElement}
                onClose={() => setAnchorElement(null)}
                anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                <div style={{ padding: '12px 4px 8px', minWidth: 260, maxWidth: 360 }}>
                    <Typography variant="subtitle2" style={{ padding: '0 16px', display: 'flex', alignItems: 'center', gap: 8 }}>
                        <ErrorOutlineIcon fontSize="small" color="error" />
                        {issues.length === 1 ? '1 field needs attention' : `${issues.length} fields need attention`}
                    </Typography>
                    <List dense={true}>
                        {issues.map((issue) => (
                            <ListItem
                                key={issue.name}
                                button={true}
                                onClick={() => {
                                    setAnchorElement(null);
                                    goToField(issue.name);
                                }}
                            >
                                <ListItemText primary={issue.label} />
                            </ListItem>
                        ))}
                    </List>
                </div>
            </Popover>
        </>
    );
};
